// 数据浏览Hook
// 管理数据浏览的状态和逻辑
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import type { 
  FilterCondition, 
  SortCondition, 
  PaginationInfo,
  BrowseDataRequest,
  BrowseDataResponse
} from '../types/field-mapping';

export interface UseBrowseDataOptions {
  initialPageSize?: number;
  autoLoad?: boolean;
  cacheEnabled?: boolean;
}

export interface UseBrowseDataReturn {
  // 数据状态
  data: any[];
  loading: boolean;
  error: string | null;
  
  // 分页状态
  pagination: PaginationInfo;
  
  // 筛选和排序状态
  filters: FilterCondition[];
  sort: SortCondition[];
  searchText: string;
  
  // 选择状态
  selectedRows: any[];
  selectedRowKeys: React.Key[];
  
  // 操作方法
  loadData: (params?: Partial<BrowseDataRequest>) => Promise<void>;
  refresh: () => Promise<void>;
  setPage: (page: number, pageSize?: number) => void;
  setFilters: (filters: FilterCondition[]) => void;
  setSort: (sort: SortCondition[]) => void;
  setSearch: (searchText: string) => void;
  clearFilters: () => void;
  clearSelection: () => void;
  setSelectedRows: (rows: any[], keys: React.Key[]) => void;
  
  // 导出方法
  exportData: (format: 'csv' | 'excel' | 'json') => Promise<void>;
}

const useBrowseData = (options: UseBrowseDataOptions = {}): UseBrowseDataReturn => {
  const {
    initialPageSize = 20,
    autoLoad = true,
    cacheEnabled = true
  } = options;

  // 数据状态
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 分页状态
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: initialPageSize,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // 筛选和排序状态
  const [filters, setFiltersState] = useState<FilterCondition[]>([]);
  const [sort, setSortState] = useState<SortCondition[]>([]);
  const [searchText, setSearchTextState] = useState('');

  // 选择状态
  const [selectedRows, setSelectedRowsState] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeysState] = useState<React.Key[]>([]);

  // 缓存
  const [cache, setCache] = useState<Map<string, any>>(new Map());

  // 生成缓存键
  const getCacheKey = useCallback((params: BrowseDataRequest): string => {
    return JSON.stringify({
      page: params.page,
      limit: params.limit,
      search: params.search,
      filters: params.filters,
      sort: params.sort
    });
  }, []);

  // 真实API调用
  const apiCall = useCallback(async (params: BrowseDataRequest): Promise<BrowseDataResponse> => {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
    const url = `${apiBaseUrl}/browse/products`;

    console.log('🌐 API Call Details:');
    console.log('  URL:', url);
    console.log('  Method: POST');
    console.log('  Params:', JSON.stringify(params, null, 2));

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      },
      body: JSON.stringify(params)
    });

    console.log('📡 Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error Response:', errorText);
      throw new Error(`API请求失败: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('📦 API Response:', result);

    if (!result.success) {
      throw new Error(result.error?.message || '获取数据失败');
    }

    return {
      success: true,
      data: {
        items: result.data.items || [],
        pagination: result.data.pagination || {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        },
        filters: result.data.filters || { applied: [], available: {} },
        sort: result.data.sort || []
      },
      executionTime: result.executionTime || 0
    };
  }, []);

  // 删除所有模拟数据 - 只使用真实D1数据库

  // 加载数据
  const loadData = useCallback(async (params?: Partial<BrowseDataRequest>) => {
    const requestParams: BrowseDataRequest = {
      page: pagination.page,
      limit: pagination.limit,
      search: searchText,
      filters,
      sort,
      ...params
    };

    console.log('🔍 Loading data with params:', requestParams);
    console.log('🔍 Filters:', filters);

    const cacheKey = getCacheKey(requestParams);

    // 检查缓存
    if (cacheEnabled && cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      setData(cachedData.items);
      setPagination(cachedData.pagination);
      console.log('📦 Using cached data');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 直接使用真实API - 不再使用任何模拟数据
      console.log('🌐 Making API call with params:', requestParams);
      const response = await apiCall(requestParams);

      if (response.success) {
        setData(response.data.items);
        setPagination(response.data.pagination);

        // 缓存结果
        if (cacheEnabled) {
          const newCache = new Map(cache);
          newCache.set(cacheKey, response.data);
          setCache(newCache);
        }

        console.log('✅ Data loaded successfully:', response.data.items.length, 'items');
        message.success(`加载了 ${response.data.items.length} 条记录`);
      } else {
        throw new Error('加载数据失败');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '未知错误';
      console.error('❌ Data loading failed:', errorMessage);
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchText, filters, sort, cache, cacheEnabled, getCacheKey, apiCall]);

  // 刷新数据
  const refresh = useCallback(async () => {
    // 清除缓存
    setCache(new Map());
    await loadData();
  }, [loadData]);

  // 设置页码
  const setPage = useCallback((page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      page,
      limit: pageSize || prev.limit
    }));
  }, []);

  // 设置筛选条件
  const setFilters = useCallback((newFilters: FilterCondition[]) => {
    setFiltersState(newFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // 重置到第一页
  }, []);

  // 设置排序条件
  const setSort = useCallback((newSort: SortCondition[]) => {
    setSortState(newSort);
  }, []);

  // 设置搜索文本
  const setSearch = useCallback((text: string) => {
    setSearchTextState(text);
    setPagination(prev => ({ ...prev, page: 1 })); // 重置到第一页
  }, []);

  // 清除筛选条件
  const clearFilters = useCallback(() => {
    setFiltersState([]);
    setSearchTextState('');
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // 清除选择
  const clearSelection = useCallback(() => {
    setSelectedRowsState([]);
    setSelectedRowKeysState([]);
  }, []);

  // 设置选择的行
  const setSelectedRows = useCallback((rows: any[], keys: React.Key[]) => {
    setSelectedRowsState(rows);
    setSelectedRowKeysState(keys);
  }, []);

  // 导出数据
  const exportData = useCallback(async (format: 'csv' | 'excel' | 'json') => {
    try {
      message.info(`正在导出 ${format.toUpperCase()} 格式...`);
      // TODO: 实现实际的导出逻辑
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(`${format.toUpperCase()} 导出成功`);
    } catch (err) {
      message.error('导出失败');
    }
  }, []);

  // 自动加载数据
  useEffect(() => {
    if (autoLoad) {
      loadData();
    }
  }, [pagination.page, pagination.limit, searchText, filters, sort]);

  return {
    // 数据状态
    data,
    loading,
    error,
    
    // 分页状态
    pagination,
    
    // 筛选和排序状态
    filters,
    sort,
    searchText,
    
    // 选择状态
    selectedRows,
    selectedRowKeys,
    
    // 操作方法
    loadData,
    refresh,
    setPage,
    setFilters,
    setSort,
    setSearch,
    clearFilters,
    clearSelection,
    setSelectedRows,
    
    // 导出方法
    exportData
  };
};

export default useBrowseData;
